import type { Cart } from './catalog.ts';
import type { ClientEvent } from './events.ts';

/**
 * Derives current cart state from the event log.
 *
 * Pure, dependency-free and shared verbatim by the phone and the server, so both always
 * agree. This replaces the mutate-in-place `scan_code` of both prototypes, which is the
 * function the handoff notes singled out as the one to get right: there, the rule "an
 * existing cart keeps its original arrival date" was a branch that could silently reset a
 * duration. Here it falls out of reading the log.
 */

export interface CartState {
  code: string;
  name: string;
  status: Cart['status'];
  /** Site of the most recent scan, or null if never scanned. */
  current_site: string | null;
  /** Start of the current *unbroken run* of scans at `current_site` — not the last scan. */
  arrived_at: string | null;
  last_seen_at: string | null;
  /** Whole days since arrival, inclusive of today. */
  days_present: number | null;
  last_missed_at: string | null;
  missed_count: number;
  /**
   * Set when the cart was reported missing more recently than it was last confirmed.
   * A later scan anywhere clears it automatically — the cart had simply moved.
   */
  missing_since: string | null;
  /** No confirmation anywhere within the staleness window. */
  stale: boolean;
}

const DAY_MS = 86_400_000;

/**
 * Total order over events.
 *
 * Ordered by device clock, tie-broken by id. Deliberately NOT by the server's
 * `received_at`: the client has no such value for events still sitting in its outbox, so
 * using it would make the phone and the server compute different answers from the same log.
 *
 * A wrong device clock is therefore handled by *detection*, not by reordering — the server
 * records the skew on arrival and flags the round for review (see MAX_CLOCK_SKEW_MINUTES).
 * Silently reordering would hide the fault; flagging it puts a human on it.
 */
export function compareEvents(a: ClientEvent, b: ClientEvent): number {
  if (a.at !== b.at) return a.at < b.at ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export interface ProjectOptions {
  now?: Date;
  staleAfterDays?: number;
}

export function projectCarts(
  carts: readonly Cart[],
  events: readonly ClientEvent[],
  opts: ProjectOptions = {},
): Map<string, CartState> {
  const now = opts.now ?? new Date();
  const staleAfterDays = opts.staleAfterDays ?? 30;
  const ordered = [...events].sort(compareEvents);

  // Scans per cart, and the set of carts scanned within each round. The latter guards
  // against a degenerate closure that lists a cart as missing which was in fact scanned in
  // that very round; a scan always beats an absence inferred over the same sweep.
  const scansByCart = new Map<string, { at: string; site: string }[]>();
  const scannedInRound = new Map<string, Set<string>>();

  // Withdrawn scans, gathered first: a retraction can arrive before the scan it withdraws
  // when pages of a sync land out of order, and the answer must not depend on that.
  const retracted = new Set<string>();
  for (const e of ordered) if (e.type === 'scan_retracted') retracted.add(e.scan_id);

  for (const e of ordered) {
    if (e.type !== 'cart_scanned' || retracted.has(e.id)) continue;
    let list = scansByCart.get(e.cart_code);
    if (!list) scansByCart.set(e.cart_code, (list = []));
    list.push({ at: e.at, site: e.site_code });

    let inRound = scannedInRound.get(e.round_id);
    if (!inRound) scannedInRound.set(e.round_id, (inRound = new Set()));
    inRound.add(e.cart_code);
  }

  /**
   * A closure is the conclusion a round reached. Reopening *withdraws* it — the round is
   * being walked again — so only a closure that still stands at the end of the log counts.
   * That is what stops a reopened-and-re-closed round from reporting its absences twice,
   * and what makes a withdrawn conclusion stop flagging carts the moment it is withdrawn.
   */
  const conclusions = new Map<string, { at: string; missing: readonly string[] }>();
  for (const e of ordered) {
    if (e.type === 'round_closed') conclusions.set(e.round_id, { at: e.at, missing: e.missing });
    else if (e.type === 'round_reopened') conclusions.delete(e.round_id);
  }

  const missesByCart = new Map<string, { at: string; count: number }>();
  // In clock order, so `missing_since` is the most recent report and not merely the last
  // one this map happened to visit.
  for (const [round_id, c] of [...conclusions].sort((a, b) => a[1].at.localeCompare(b[1].at))) {
    const scanned = scannedInRound.get(round_id);
    for (const code of c.missing) {
      if (scanned?.has(code)) continue;
      const prev = missesByCart.get(code);
      missesByCart.set(code, { at: c.at, count: (prev?.count ?? 0) + 1 });
    }
  }

  const out = new Map<string, CartState>();
  for (const cart of carts) {
    const scans = scansByCart.get(cart.code) ?? [];
    const miss = missesByCart.get(cart.code);

    let current_site: string | null = null;
    let arrived_at: string | null = null;
    let last_seen_at: string | null = null;

    if (scans.length > 0) {
      const last = scans[scans.length - 1]!;
      current_site = last.site;
      last_seen_at = last.at;
      // Walk back while the cart stayed at this same site; the first scan of that run is
      // the arrival. This is what preserves an accumulating duration across re-scans.
      arrived_at = last.at;
      for (let i = scans.length - 2; i >= 0; i--) {
        const s = scans[i]!;
        if (s.site !== current_site) break;
        arrived_at = s.at;
      }
    }

    const missing_since =
      miss && (!last_seen_at || miss.at > last_seen_at) ? miss.at : null;

    out.set(cart.code, {
      code: cart.code,
      name: cart.name,
      status: cart.status,
      current_site,
      arrived_at,
      last_seen_at,
      days_present:
        arrived_at === null
          ? null
          : Math.max(0, Math.floor((now.getTime() - Date.parse(arrived_at)) / DAY_MS)),
      last_missed_at: miss?.at ?? null,
      missed_count: miss?.count ?? 0,
      missing_since,
      stale:
        cart.status === 'active' &&
        (last_seen_at === null ||
          now.getTime() - Date.parse(last_seen_at) > staleAfterDays * DAY_MS),
    });
  }
  return out;
}

/** Carts currently recorded at a site. Only `active` ones — a cart already written off is
 *  not expected anywhere, and must not be re-reported as missing. */
export function cartsAtSite(
  states: ReadonlyMap<string, CartState>,
  site_code: string,
): CartState[] {
  const out: CartState[] = [];
  for (const s of states.values()) {
    if (s.status === 'active' && s.current_site === site_code) out.push(s);
  }
  return out.sort((a, b) => a.code.localeCompare(b.code));
}
